import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import {
  CicloFacturacion,
  EnumPeriodoLiquidacion,
  EnumTipoCalculoConcepto,
  EnumTipoCantidad,
  EnumUnidadMedida
} from '../../api'
import {
  CalculationConceptType,
  FlexibilityGroup,
  FlexibilityModel,
  FlexibilitySubtype,
  FlexibilitySubtypeQuantityTypes,
  NewFlexibilityRequest,
  PatchPercentageRequest
} from '../../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { NewPriceModelAPI } from '../../api/Argentia/endpoints/pricing/model/prices'
import { MeasureUnit } from '../../api/Pioneer/model/MeasureUnit'
import { EnumPeriodoCalculo } from '../../api/oData/enums/enumPeriodoCalculo'
import { enumEnumToValueNumber, generateUniqueKeyWithNoPrefix, toLocaleDate } from '../../helpers'
import { GridRows } from '../../helpers/gridValuesGenerator/gridValuesGenerator'
import { ContractQuantityFormModel } from '../contracts/contracts.model'
import { PriceDetails } from '../pricing/pricing.Model'
import { mapPriceModelAPIToPriceDetails } from '../pricing/pricing.mapper'
import {
  CalculationTypeSelectOption,
  FlexibilityDetails,
  FlexibilityTab,
  Limit,
  LimitDetail,
  LimitForm,
  NewFlexibility,
  NewLimitDetail,
  QuantityFlexibility
} from './flexibility.Model'
import { EnumPriceType } from '../../api/oData/enums/enumPriceType'

export const flexibilityModelToFlexibilityDetailsMapper = (
  flexibility: FlexibilityModel
): FlexibilityDetails => {
  return {
    id: flexibility.id,
    calculationType: enumEnumToValueNumber(EnumTipoCalculoConcepto, flexibility.calculationType),
    subtype: flexibility.subtype,
    description: flexibility.description,
    period: `${toLocaleDate(flexibility.startDate)} - ${toLocaleDate(flexibility.endDate)}`,
    contractId: flexibility.contractId,
    contractCode: flexibility.contractCode,
    startDate: flexibility.startDate,
    endDate: flexibility.endDate,
    signDate: flexibility.signDate,
    flexibilityGroupId: flexibility.flexibilityGroupId,
    flexibilityGroup: flexibility.flexibilityGroup,
    calculationPeriod: enumEnumToValueNumber(EnumPeriodoCalculo, flexibility.calculationPeriod),
    settlementPeriod: enumEnumToValueNumber(EnumPeriodoLiquidacion, flexibility.settlementPeriod),
    invoicingCycleId: flexibility.invoicingCycleId,
    invoiceCycleDesc: flexibility.invoicingCycle,
    comments: flexibility.comments,
    price: mapPriceModelAPIToPriceDetails(flexibility?.price)
  } as FlexibilityDetails
}

export const postFlexibilityMapper = (newFlexibility: NewFlexibility): NewFlexibilityRequest => {
  return {
    ...newFlexibility,
    flexibilityGroup: newFlexibility.flexibilityGroup,
    startDate: moment(newFlexibility.startDate).format('YYYY-MM-DD'),
    endDate: moment(newFlexibility.endDate).format('YYYY-MM-DD'),
    signDate: newFlexibility.signDate ? moment(newFlexibility.signDate).format('YYYY-MM-DD') : null
  }
}

export const patchFlexibilityLimitByIDRequestPatch = (
  percentage: number
): PatchPercentageRequest => {
  return {
    inferiorPercentage: percentage
  }
}

export const calculationConceptTypesToFlexibilityTabListMapper = (
  types: CalculationConceptType[]
): FlexibilityTab[] => {
  return types.map(calculationConceptTypeToFlexibilityTabItemMapper)
}

export const calculationConceptTypeToFlexibilityTabItemMapper = (
  type: CalculationConceptType
): FlexibilityTab => {
  return {
    name: type.description,
    value: type.id
  }
}

export const calculationConceptTypeToSelectOptionMapper = (
  calculationConceptTypes: CalculationConceptType[]
): CalculationTypeSelectOption[] => {
  return calculationConceptTypes.map(type => ({
    value: type.id,
    label: type.description,
    currency: type.currency,
    measureUnit: type.measureUnit
  }))
}

export const subtypeQuantityTypesToSelectOptionMapper = (
  options: FlexibilitySubtypeQuantityTypes
): SelectOption[] => {
  return options?.quantityTypes
    ? options.quantityTypes.map(type => ({
        value: type.id,
        label: type.description
      }))
    : []
}

export const flexibilitySubtypeToSelectOptionMapper = (
  subtypes: FlexibilitySubtype[]
): SelectOption[] => {
  return subtypes.map(type => ({ value: type.id, label: type.description }))
}

export const flexibilityGroupToSelectOptionMapper = (
  subtypes: FlexibilityGroup[]
): SelectOption[] => {
  return subtypes.map(type => ({ value: type.id, label: type.description }))
}

export const invoiceCyclesToSelectOptionMapper = (cycles: CicloFacturacion[]): SelectOption[] => {
  return cycles.map(cycle => ({ value: cycle.idCicloFacturacion, label: cycle.descripcion ?? '' }))
}

export const priceDetailsToPostFlexibilityRequest = (price: PriceDetails): NewPriceModelAPI => {
  return {
    idEmpresa: '1',
    commodity: 1,
    version: 1,
    priceType: enumEnumToValueNumber(EnumPriceType, EnumPriceType.Formula),
    priceCalculationType: price.priceCalculationType,
    precioUnitario: price.precioUnitario,
    redondeo: price.redondeo,
    expression: price.priceExpression
  }
}

export const contractQuantityConsolidateMapper = (
  contractQuantity: Limit
): ContractQuantityFormModel => {
  return {
    ...contractQuantity,
    measureUnit: contractQuantity?.measureUnit
      ? enumEnumToValueNumber(EnumUnidadMedida, contractQuantity.measureUnit)
      : undefined
  }
}

export const quantityInferiorLimitConsolidateMapper = (
  quantityFlexibility: QuantityFlexibility
): LimitForm => {
  return {
    ...quantityFlexibility.inferiorLimit,
    percentage: quantityFlexibility.inferiorPercentage,
    quantityType: quantityFlexibility.inferiorQuantityType
      ? enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility.inferiorQuantityType)
      : 9999,
    measureUnit: quantityFlexibility?.inferiorLimit?.measureUnit
      ? enumEnumToValueNumber(EnumUnidadMedida, quantityFlexibility.inferiorLimit.measureUnit)
      : undefined
  }
}

export const quantitySuperiorLimitConsolidateMapper = (
  quantityFlexibility: QuantityFlexibility
): LimitForm => {
  return {
    ...quantityFlexibility.superiorLimit,
    percentage: quantityFlexibility.superiorPercentage,
    quantityType: quantityFlexibility.superiorQuantityType
      ? enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility.superiorQuantityType)
      : 9999,
    measureUnit: quantityFlexibility?.superiorLimit?.measureUnit
      ? enumEnumToValueNumber(EnumUnidadMedida, quantityFlexibility.superiorLimit.measureUnit)
      : undefined
  }
}

export const gridRowToLimitDetailsList = (generatedData: GridRows[]): NewLimitDetail[] => {
  return generatedData.map((item, idx) => {
    return {
      tempId: generateUniqueKeyWithNoPrefix(),
      startDate: moment(item.startDate).toDate(),
      endDate: moment(item.endDate).toDate(),
      quantity: item.quantity
    } as NewLimitDetail
  })
}

export const limitIsFreeOrContractType = (limit: Limit | null) => {
  return limit
    ? enumEnumToValueNumber(EnumTipoCantidad, limit.quantityType) === EnumTipoCantidad.Libre ||
        enumEnumToValueNumber(EnumTipoCantidad, limit.quantityType) ===
          EnumTipoCantidad.Libre_sin_Fijacion ||
        enumEnumToValueNumber(EnumTipoCantidad, limit.quantityType) ===
          EnumTipoCantidad.Capacidad_Contractual ||
        enumEnumToValueNumber(EnumTipoCantidad, limit.quantityType) ===
          EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
    : false
}

export const limitIsFree = (limit: Limit | null) => {
  return limit
    ? enumEnumToValueNumber(EnumTipoCantidad, limit.quantityType) === EnumTipoCantidad.Libre ||
        enumEnumToValueNumber(EnumTipoCantidad, limit.quantityType) ===
          EnumTipoCantidad.Libre_sin_Fijacion
    : false
}

export const consolidateQuantityFlexibilityResponse = (
  qtyFlexibility: QuantityFlexibility
): QuantityFlexibility => {
  const inferiorLimitIsFreeOrContract = qtyFlexibility.inferiorLimit
    ? enumEnumToValueNumber(EnumTipoCantidad, qtyFlexibility.inferiorLimit.quantityType) ===
        EnumTipoCantidad.Libre ||
      enumEnumToValueNumber(EnumTipoCantidad, qtyFlexibility.inferiorLimit.quantityType) ===
        EnumTipoCantidad.Libre_sin_Fijacion ||
      enumEnumToValueNumber(EnumTipoCantidad, qtyFlexibility.inferiorLimit.quantityType) ===
        EnumTipoCantidad.Capacidad_Contractual ||
      enumEnumToValueNumber(EnumTipoCantidad, qtyFlexibility.inferiorLimit.quantityType) ===
        EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
    : false

  const superiorLimitIsFreeOrContract = qtyFlexibility.superiorLimit
    ? enumEnumToValueNumber(EnumTipoCantidad, qtyFlexibility.superiorLimit.quantityType) ===
        EnumTipoCantidad.Libre ||
      enumEnumToValueNumber(EnumTipoCantidad, qtyFlexibility.superiorLimit.quantityType) ===
        EnumTipoCantidad.Libre_sin_Fijacion ||
      enumEnumToValueNumber(EnumTipoCantidad, qtyFlexibility.superiorLimit.quantityType) ===
        EnumTipoCantidad.Capacidad_Contractual ||
      enumEnumToValueNumber(EnumTipoCantidad, qtyFlexibility.superiorLimit.quantityType) ===
        EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
    : false

  return qtyFlexibility
    ? {
        ...qtyFlexibility,
        inferiorLimit: qtyFlexibility.inferiorLimit
          ? {
              ...qtyFlexibility.inferiorLimit,
              details:
                qtyFlexibility.inferiorLimit.quantityType && !inferiorLimitIsFreeOrContract
                  ? null
                  : qtyFlexibility.inferiorLimit.details
            }
          : qtyFlexibility.inferiorLimit,
        superiorLimit: qtyFlexibility.superiorLimit
          ? {
              ...qtyFlexibility.superiorLimit,
              details:
                qtyFlexibility.superiorLimit.quantityType && !superiorLimitIsFreeOrContract
                  ? null
                  : qtyFlexibility.superiorLimit.details
            }
          : qtyFlexibility.superiorLimit
      }
    : ({} as QuantityFlexibility)
}

export const quantityFlexibilityToRequest = (
  quantityFlexibility: QuantityFlexibility,
  quantityFlexibilityDB: QuantityFlexibility,
  isGroup: boolean
): QuantityFlexibility => {
  const quantityTypeInferior =
    quantityFlexibility?.inferiorQuantityType ??
    (quantityFlexibility?.inferiorLimit?.quantityType &&
    enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility?.inferiorLimit?.quantityType) !==
      9999
      ? enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility?.inferiorQuantityType)
      : null)

  const quantityTypeSuperior =
    quantityFlexibility.superiorQuantityType ??
    (quantityFlexibility?.superiorLimit?.quantityType &&
    enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility?.superiorLimit?.quantityType) !==
      9999
      ? enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility?.superiorQuantityType)
      : null)

  const createNewSuperiorLimit =
    enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility.superiorLimit?.quantityType) ===
      EnumTipoCantidad.Capacidad_Contractual ||
    enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility.superiorLimit?.quantityType) ===
      EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
      ? null
      : quantityTypeSuperior !==
        enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibilityDB.superiorLimit?.quantityType)

  const createNewInferiorLimit =
    enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility.inferiorLimit?.quantityType) ===
      EnumTipoCantidad.Capacidad_Contractual ||
    enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibility.inferiorLimit?.quantityType) ===
      EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
      ? null
      : quantityTypeInferior !==
        enumEnumToValueNumber(EnumTipoCantidad, quantityFlexibilityDB.inferiorLimit?.quantityType)

  if (
    (!quantityTypeInferior || (quantityTypeInferior && quantityTypeInferior === 9999)) &&
    !quantityFlexibility.inferiorQuantityType &&
    (!quantityTypeSuperior || (quantityTypeSuperior && quantityTypeSuperior === 9999)) &&
    !quantityFlexibility.superiorQuantityType
  ) {
    return {
      inferiorLimit: null,
      superiorLimit: null
    }
  }

  const inferiorQuantityType =
    quantityTypeInferior ?? quantityFlexibility.inferiorLimit?.quantityType
  const superiorQuantityType =
    quantityTypeSuperior ?? quantityFlexibility.superiorLimit?.quantityType

  const isGroupFlexWithContractualQuantitiesInferior =
    isGroup &&
    (inferiorQuantityType === EnumTipoCantidad.Capacidad_Contractual ||
      inferiorQuantityType === EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion)

  const isGroupFlexWithContractualQuantitiesSuperior =
    isGroup &&
    (superiorQuantityType === EnumTipoCantidad.Capacidad_Contractual ||
      superiorQuantityType === EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion)

  return {
    idQuantityFlex: quantityFlexibility.idQuantityFlex,
    inferiorPercentage: quantityFlexibility.inferiorPercentage,
    inferiorQuantityType: inferiorQuantityType,
    inferiorLimit:
      quantityTypeInferior &&
      quantityTypeInferior !== 9999 &&
      !isGroupFlexWithContractualQuantitiesInferior
        ? {
            id: createNewInferiorLimit ? undefined : quantityFlexibility.inferiorLimit?.id,
            quantityType: quantityTypeInferior,
            measureUnit: quantityFlexibility.inferiorLimit?.measureUnit,
            details:
              quantityTypeInferior !== EnumTipoCantidad.Capacidad_Contractual &&
              quantityTypeInferior !== EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion &&
              quantityFlexibility?.inferiorLimit?.details &&
              quantityFlexibility.inferiorLimit.details.length > 0
                ? limitDetailsMapper(quantityFlexibility.inferiorLimit.details)
                : null
          }
        : null,
    superiorPercentage: quantityFlexibility.superiorPercentage,
    superiorQuantityType: quantityTypeSuperior ?? quantityFlexibility.superiorLimit?.quantityType,
    superiorLimit:
      quantityTypeSuperior &&
      quantityTypeSuperior !== 9999 &&
      !isGroupFlexWithContractualQuantitiesSuperior
        ? {
            id: createNewSuperiorLimit ? undefined : quantityFlexibility.superiorLimit?.id,
            quantityType: quantityTypeSuperior,
            measureUnit: quantityFlexibility.superiorLimit?.measureUnit,
            details:
              quantityTypeSuperior !== EnumTipoCantidad.Capacidad_Contractual &&
              quantityTypeSuperior !== EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion &&
              quantityFlexibility?.superiorLimit?.details &&
              quantityFlexibility.superiorLimit.details.length > 0
                ? limitDetailsMapper(quantityFlexibility.superiorLimit.details)
                : null
          }
        : null
  }
}

export const limitDetailsMapper = (details: LimitDetail[]): LimitDetail[] => {
  return details.map(detail => ({
    idDetail: detail.idDetail,
    quantity: detail.quantity,
    startDate: detail.startDate,
    endDate: detail.endDate
  }))
}

export const measureUnitToSelectOptions = (measureUnits: MeasureUnit[]): SelectOption[] => {
  return measureUnits.map(measureUnit => ({
    value: measureUnit.id,
    label: measureUnit.descMeasureUnit
  }))
}

const isContractual = (quantityType: number): boolean => {
  return (
    quantityType === EnumTipoCantidad.Capacidad_Contractual ||
    quantityType === EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
  )
}
